// eslint-disable-next-line no-restricted-imports -- This background query runs in entry points without ReportErrorContextProvider.
import {reportError} from '@github-ui/failbot'
import type {IssueFieldSelection} from '@github-ui/issue-fields-shared/issue-field'
import {parsePinnedIssueFields} from '@github-ui/issue-fields-shared/issue-field'
import {useQuery} from '@github-ui/react-query'
import {useEffect} from 'react'

import {
  fetchIssueFormSecondary,
  issueFormSecondaryQueryKey,
  type IssueFormSecondaryRepository,
} from './fetch-issue-form-secondary'

export type IssueCreateStatus = {
  id: string
  name: string
  category: {name: string; state: 'OPEN' | 'CLOSED'}
}

type useIssueFormSecondaryQueryProps = {
  owner: string
  repo: string
}

export type SecondaryQueryData = {
  securityPolicyUrl: string | null
  contributingFileUrl: string | null
  codeOfConductFileUrl: string | null
  supportFileUrl: string | null
  ownerPinnedFields: readonly IssueFieldSelection[] | null
  fieldVisibilityMap: Record<string, string>
  ownerHasIssueFields: boolean
  orgHasIssueTypes: boolean
  // Empty while statuses are off for the owner or viewer.
  openStatuses: readonly IssueCreateStatus[]
}

const SAFE_DEFAULTS: SecondaryQueryData = {
  securityPolicyUrl: null,
  contributingFileUrl: null,
  codeOfConductFileUrl: null,
  supportFileUrl: null,
  ownerPinnedFields: null,
  fieldVisibilityMap: {},
  ownerHasIssueFields: true,
  orgHasIssueTypes: true,
  openStatuses: [],
}

type OwnerWithIssueTypes = {
  readonly issueTypes?: {
    readonly totalCount: number
  } | null
}

function hasIssueTypes(owner: object): owner is OwnerWithIssueTypes {
  return 'issueTypes' in owner
}

// New issues can't start closed, so only statuses in an open category are offered.
function pickOpenStatuses(
  owner: {readonly issueStatuses?: {readonly nodes?: ReadonlyArray<IssueCreateStatus | null> | null} | null} | null,
) {
  return (owner?.issueStatuses?.nodes ?? []).filter(
    (status): status is IssueCreateStatus => status?.category.state === 'OPEN',
  )
}

function mapSecondaryQueryData(repository: IssueFormSecondaryRepository): SecondaryQueryData {
  const owner = 'owner' in repository && repository.owner ? repository.owner : null
  const pinnedIssueFieldNodes = repository.pinnedIssueFields?.nodes
  const ownerPinnedFields = parsePinnedIssueFields(pinnedIssueFieldNodes ?? undefined)

  const fieldVisibilityMap: Record<string, string> = {}
  for (const node of repository.issueFields?.nodes ?? []) {
    if (node && 'id' in node && 'visibility' in node) {
      fieldVisibilityMap[node.id] = node.visibility
    }
  }

  return {
    securityPolicyUrl: repository.securityPolicyUrl ?? null,
    contributingFileUrl: repository.contributingFileUrl ?? null,
    codeOfConductFileUrl: repository.codeOfConductFileUrl ?? null,
    supportFileUrl: repository.supportFileUrl ?? null,
    ownerPinnedFields,
    fieldVisibilityMap,
    ownerHasIssueFields: (owner?.issueFieldsEnabled ?? false) && (repository.issueFields?.totalCount ?? 0) > 0,
    orgHasIssueTypes: (owner && hasIssueTypes(owner) ? (owner.issueTypes?.totalCount ?? 0) : 0) > 0,
    openStatuses: pickOpenStatuses(owner),
  }
}

export function useIssueFormSecondaryQuery({owner, repo}: useIssueFormSecondaryQueryProps): SecondaryQueryData | null {
  const serverQuery = useQuery({
    queryKey: issueFormSecondaryQueryKey(owner, repo),
    queryFn: ({signal}) => fetchIssueFormSecondary(owner, repo, signal),
    select: mapSecondaryQueryData,
    retry: false,
  })

  useEffect(() => {
    if (serverQuery.error) reportError(serverQuery.error)
  }, [serverQuery.error])

  if (serverQuery.isError) return SAFE_DEFAULTS
  return serverQuery.data ?? null
}
